import './Experience.scss';
import Card from '../Card';
import Section from '../Section';
import { img_misty, img_pmw } from '../../images64';


const getCodeMarkup = (text: string) => <code className="pill">{text}</code>;

const getDateContainerMarkup = (from: string, to: string, duration: string) => {
    return <div className='date-container'>
        <span className='date-from text'>{from}</span>
        <hr className='date-separator'/>
        <span className='date-duration text visible-tablet'>{duration}</span>
        <hr className='date-separator visible-tablet'/>
        <span className='date-to text'>{to}</span>
    </div>;
};

const whovaExpMarkup = <a className='experience-link' href="https://whova.com/" target='_blank' rel="noreferrer">
        <Card className='experience-container' showIcon={true}>
            <div className='title-wrapper '>
                <h3 className='experience-title'>Software Engineer</h3>
                <p className='experience-location'>
                    <span className='company'>
                        <img className="company-logo" src="images/logos/whova.png" alt="whova company logo"/>
                        Whova
                    </span>
                    &nbsp;• San Diego, CA
                </p>
            </div>
            {getDateContainerMarkup('July 2024', 'PRESENT', '1+ years')}
            <div className='description-wrapper'>
                <p className='text'>
                    Designed and built a <span className="text -bold">content-based recommender system</span> leveraging OpenAI <span className="text -bold">embedding models</span> to improve personalization and relevance of recommendations. 
                    Developed and maintained <span className="text -bold">full stack product features</span> implemented using a variety of design patterns over a REST API with Python (Django, DRF) 
                    connected to interactive UIs with React, Formik and TanStack Query (React Query). Applied <span className="text -bold">prompt engineering</span> techniques and created lightweight wrappers for 
                    LLM SDKs (ChatGPT, Gemini) to streamline and automate complex prompting workflows. Delivered <span className="text -bold">7 large-scale web application features </span> 
                    through collaboration with engineering, product and design teams.
                    <br/>
                    <br/>
                    {['OpenAI GPT', 'Google GenAI', 'Django', 'DRF', 'React', 'Formik', 'TanStack Query', 'REST API'].map(s => getCodeMarkup(s))}
                </p>
            </div>
        </Card>
    </a>;


const uwExpMarkup = <a className='experience-link' href="https://peopleandrobots.wisc.edu/" target='_blank' rel="noreferrer">
        <Card className='experience-container' showIcon={true}>
            <div className='title-wrapper'>
                <h3 className='experience-title'>Software Engineer</h3>
                    <p className='experience-location'>
                        <span className='company'>
                            <img className="company-logo" src="images/logos/people_and_robots_lab.jpg" alt="people and robots lab logo"/>
                            People & Robots Lab
                        </span>
                        &nbsp;• Madion, WI
                    </p>
            </div>
            {getDateContainerMarkup('June 2023', 'Sept 2023', '4 months')}
            <div className='description-wrapper'>
                <img className='img visible-tablet -hidden' src={img_misty}/>
                <p className='text'>
                    Created an engaging human-robot interaction to aid children's reading. Built a multi-container <span className="text -bold">Docker applications</span> for rapid deployment of source code to raspberry pi. 
                    Polished the robot behavior display system for improved emotional display and easier modifications. <br/>
                    Refactored code and integrated Python decorators for automated logging of method calls to trace function executions and optimize debugging.
                    <br/>
                    <br/>
                    {getCodeMarkup('Docker')} {getCodeMarkup('Python')} {getCodeMarkup('ROS')} {getCodeMarkup('BASH')}
                </p>
            </div>
        </Card>
    </a>;

const pmwExpMarkup = <a className='experience-link' href='https://www.postermywall.com/index.php/m/email-campaigns' target='_blank' rel="noreferrer">
        <Card className='experience-container' showIcon={true}>
            <div className='title-wrapper'>
                <h3 className='experience-title'>Full Stack Software Engineer</h3>
                <p className='experience-location'>
                    <span className='company'>
                        <img className="company-logo" src="images/logos/pmw.png" alt="postermywall company logo"/>
                        PosterMyWall
                    </span>
                    &nbsp;• Lahore, PK
                </p>
            </div>
            {getDateContainerMarkup('July 2021', 'July 2022', '1 year')}
            <div className='description-wrapper'>
                <p className='text'>
                    Developed and launched a new <span className="text -bold">drag-and-drop</span> WYSIWYG <span className="text -highlight">HTML editor</span> from prototype to deployment. 
                    Introduced new features including <span className="text -bold">HTML5 canvas to PDF export</span> and dynamic image resizing for a design tool. 
                    Upgraded front-end interfaces and back-end code to improve existing web application features. 
                    Collaborated with designers in an agile workflow to polish the user experience of features across the website.
                    <br/>
                    <br/>
                    {getCodeMarkup('React')} {getCodeMarkup('JavaScript')} {getCodeMarkup('PHP')} {getCodeMarkup('HTML/SCSS')}
                    {getCodeMarkup('Fabric.js')} {getCodeMarkup('GrapesJS')} {getCodeMarkup('REST API')}
                </p>
                <img className='img visible-tablet -hidden' src={img_pmw}/>
            </div>
        </Card>
    </a>;

const mindstormExpMarkup = <a className='experience-link' href='https://mlabs.co/game-jams/rookie-gamejam-2020/' target='_blank' rel="noreferrer">
        <Card className='experience-container' showIcon={true}>
            <div className='title-wrapper'>
                <h3 className='experience-title'>Game Programming Intern</h3>
                <p className='experience-location'>
                    <span className='company'>
                        <img className="company-logo" src="images/logos/mindstorm.png" alt="mindstorm studios company logo"/>
                        Mindstorm Studios
                    </span>
                    &nbsp;• Lahore, PK
                </p>
            </div>
            {getDateContainerMarkup('Aug 2020', 'Oct 2020', '2 months')}
            <div className='description-wrapper'>
                <p className='text'>
                    Designed and created a 3D arcade mobile game in Unity, which placed among <span className="text -highlight">top 3</span> games in the Rookie game jam
                    Directed a multi-disciplinary team of 4 people and delegated tasks as part of <span className="text -bold">team leadership</span> and project management
                    <br/>
                    <br/>
                    {getCodeMarkup('C#')} {getCodeMarkup('Unity')} {getCodeMarkup('Game Design')} {getCodeMarkup('Game Development')}
                </p>
                <img className='img visible-tablet -hidden' src={img_pmw}/>
            </div>
        </Card>
    </a>;

export default function Experience() {
    return <Section title='Experience'>
        {whovaExpMarkup}
        <br/>
        <br/>
        {uwExpMarkup}
        <br/>
        <br/>
        {pmwExpMarkup}
        <br/>
        <br/>
        {mindstormExpMarkup}
    </Section>;
};